import classNames from 'classnames';

import Checkbox from '@/components/atoms/Checkbox';

import {
    applySelection,
    type CheckboxMenuCheckState,
    type CheckboxMenuOption,
    type CheckboxMenuSection,
    sectionCheckState,
} from './checkboxMenuModel';

import styles from './CheckboxMenu.module.scss';

interface CheckboxMenuRowProps {
    label: string;
    description?: string;
    checked: CheckboxMenuCheckState;
    onCheckedChange: (checked: boolean) => void;
    variant: 'group' | 'grouped' | 'plain';
}

function CheckboxMenuRow(props: CheckboxMenuRowProps) {
    const { label, description, checked, onCheckedChange, variant } = props;

    // The label wraps the checkbox so a click anywhere on the row toggles it and
    // the text becomes the checkbox's accessible name.
    return (
        <label
            className={classNames(styles.row, {
                [styles.groupRow]: variant === 'group',
                [styles.groupedRow]: variant === 'grouped',
            })}
            title={description === undefined ? label : `${label} ${description}`}>
            <Checkbox checked={checked} onCheckedChange={onCheckedChange} />
            <span className={styles.rowLabel}>{label}</span>
            {description !== undefined && (
                <span className={styles.rowDescription}>{description}</span>
            )}
        </label>
    );
}

interface CheckboxMenuRowsProps<TValue extends string> {
    section: CheckboxMenuSection<TValue>;
    /** The options the group row reflects and acts on, when not the ones shown. */
    groupOptions?: readonly CheckboxMenuOption<TValue>[];
    selected: readonly TValue[];
    selectedSet: ReadonlySet<string>;
    onChange: (values: TValue[]) => void;
}

/**
 * One section: the group row, when there is a group, then its options. Unless
 * given `groupOptions`, the group row acts on the options shown, so while a
 * search narrows the section it selects or clears only the visible matches.
 */
export default function CheckboxMenuRows<TValue extends string>(
    props: CheckboxMenuRowsProps<TValue>
) {
    const { section, groupOptions, selected, selectedSet, onChange } = props;
    const { group, options } = section;

    const scopeOptions = groupOptions ?? options;
    const groupValues = scopeOptions.map((option) => option.value);

    const optionRows = options.map((option) => (
        <CheckboxMenuRow
            key={option.value}
            label={option.label}
            description={option.description}
            variant={group ? 'grouped' : 'plain'}
            checked={selectedSet.has(option.value)}
            onCheckedChange={(checked) =>
                onChange(applySelection(selected, [option.value], checked))
            }
        />
    ));

    if (!group) return <div className={styles.section}>{optionRows}</div>;

    return (
        <fieldset className={styles.section} aria-label={group.label}>
            <CheckboxMenuRow
                label={group.label}
                variant="group"
                checked={sectionCheckState(scopeOptions, selectedSet)}
                onCheckedChange={(checked) =>
                    onChange(applySelection(selected, groupValues, checked))
                }
            />
            {optionRows}
        </fieldset>
    );
}
